import { FC, ReactNode } from 'react';

import CloseIcon from '@mui/icons-material/Close';
import {
  Chip,
  FormControl,
  FormHelperText,
  IconButton,
  InputLabel,
  MenuItem,
  Select,
  SelectChangeEvent,
  SelectProps,
  Typography,
} from '@mui/material';
import { Stack } from '@mui/system';

type Option = {
  label: string;
  value: string;
  disabled?: boolean;
  shouldHideDeleteIcon?: boolean;
};

type WaveUIMultiSelectProps = Omit<SelectProps<string[]>, 'renderValue'> & {
  name: string;
  label: string;
  value?: string[];
  options: Option[];
  placeholder: string;
  helperText?: string;
  error?: boolean;
  onChange?: (event: SelectChangeEvent<string[]>) => void;
  renderOption?: (option: Option) => ReactNode;
  onDeleteSelected?: (value: string) => void;
};

const WaveUIMultiSelect: FC<WaveUIMultiSelectProps> = ({
  name,
  label,
  value,
  onChange,
  options,
  placeholder,
  helperText,
  renderOption,
  onDeleteSelected,
  fullWidth = true,
  error,
  ...props
}) => {
  return (
    <FormControl
      variant='outlined'
      required={props.required}
      error={error}
      fullWidth={fullWidth}
    >
      <InputLabel htmlFor={`id_${name}`}>{label}</InputLabel>
      <Select
        {...props}
        multiple
        name={name}
        label={label}
        inputProps={{ id: `id_${name}` }}
        value={value}
        onChange={onChange}
        displayEmpty={true}
        renderValue={(values: string[]) =>
          values?.length ? (
            <Stack
              sx={{
                display: 'flex',
                flexDirection: 'row',
                flexWrap: 'wrap',
                gap: 1,
              }}
            >
              {values.map((value) => {
                const option = options.find((option) => option.value === value);

                const { label, shouldHideDeleteIcon } = option || {};

                return shouldHideDeleteIcon ? (
                  <Chip key={value} label={label} />
                ) : (
                  <Chip
                    key={value}
                    label={value}
                    clickable
                    deleteIcon={
                      <IconButton
                        onMouseDown={(e) => e.stopPropagation()}
                        size='small'
                      >
                        <CloseIcon sx={{ width: 16, height: 16 }} />
                      </IconButton>
                    }
                    onDelete={(e: MouseEvent) => {
                      e.preventDefault();
                      onDeleteSelected?.(value);
                    }}
                    sx={{
                      maxWidth: 'max-content',
                    }}
                  />
                );
              })}
            </Stack>
          ) : (
            <Typography variant='body1' color='grey.400'>
              {placeholder}
            </Typography>
          )
        }
        autoComplete='off'
      >
        {options.map((option) => (
          <MenuItem
            key={option.value}
            value={option.value}
            disabled={option.disabled}
          >
            {(renderOption && renderOption(option)) || option.label}
          </MenuItem>
        ))}
      </Select>
      {helperText && <FormHelperText>{helperText}</FormHelperText>}
    </FormControl>
  );
};

export default WaveUIMultiSelect;
